// The checkout drawer's hold timer on the real clock: every rule is in
// hold-timer-logic.ts (pure, with its own spec); this file only ticks, listens
// for the guest's typing and tapping, and makes the calls it says to make.
//
//   * One tick a second (and one when the tab comes back into view) re-judges
//     the state; only a change in what is shown re-renders, so the rest of
//     the checkout never re-renders on the clock.
//   * A key press, tap or edit in the checkout form is activity, and is
//     judged straight away: a keystroke at 3:59 asks for more time then, not
//     on the next tick. A tap counts only on a control (a field, a button, a
//     checkbox's label): a finger that lands on the form's background to
//     scroll it is not the guest filling it in. "Back to your visit" (marked
//     data-hold-passive) never counts: the guest is leaving.
//   * The device's clock is checked against the page's monotonic clock on
//     every tick. When it jumps (set by hand, or the device woke from sleep)
//     the server's clock is read again, so the skew is never stale.
//   * At most one call is in flight. A new set of holds (the cart's ids
//     changed) starts again with a fresh read; an answer to a call made for
//     the old set is dropped.
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type { CartItem } from "./cart";
import { extendCartHold, getCartHoldState } from "./booking";
import {
  applyExtend,
  applyRead,
  cartHoldIds,
  clockJumped,
  rereadClock,
  hasSpaItem,
  initialKeepAlive,
  markActivity,
  nextAction,
  startCall,
  timerView,
  TICK_MS,
  type HoldTimerView,
  type KeepAliveState,
} from "./hold-timer-logic";

const ACTIVITY_EVENTS = ["keydown", "pointerdown", "input", "change"] as const;
/** What a tap has to land on to count: a control, or a label for one. */
const TAP_TARGETS =
  "input, textarea, select, button, summary, label, a[href], [contenteditable], [role='button'], [role='checkbox'], [role='radio'], [role='switch'], [tabindex]:not([tabindex='-1'])";

/** Is this event the guest filling in the checkout (not leaving it, not scrolling it)? */
function isActivity(e: Event): boolean {
  const target = e.target instanceof Element ? e.target : null;
  if (target?.closest("[data-hold-passive]")) return false;
  if (e.type === "pointerdown") return !!target?.closest(TAP_TARGETS);
  return true;
}

export function useHoldKeepAlive(
  items: readonly CartItem[],
  formRef: RefObject<HTMLElement | null>,
): HoldTimerView {
  const pageIds = useMemo(() => cartHoldIds(items), [items]);
  const spa = hasSpaItem(items);
  const signature = pageIds.join("|");

  const idsRef = useRef(pageIds);
  idsRef.current = pageIds;
  const sigRef = useRef(signature);
  sigRef.current = signature;
  const stateRef = useRef<KeepAliveState>(initialKeepAlive({ hasSpaItem: spa }));
  // Bumped on every restart: an answer for an older set of holds is dropped.
  const genRef = useRef(0);
  const cartKey = `${signature}#${spa ? "spa" : ""}`;
  const cartKeyRef = useRef(cartKey);
  if (cartKeyRef.current !== cartKey) {
    cartKeyRef.current = cartKey;
    genRef.current += 1;
    stateRef.current = {
      ...initialKeepAlive({ hasSpaItem: spa }),
      lastActivityAt: stateRef.current.lastActivityAt,
    };
  }

  const [view, setView] = useState<HoldTimerView>(() => timerView(stateRef.current, Date.now()));
  const viewKey = useRef(JSON.stringify(view));

  // The engine lives in refs so the tick, the listeners and the calls always
  // see the latest state without re-subscribing.
  const engine = useRef<{ step: () => void } | null>(null);
  useEffect(() => {
    let alive = true;
    const show = () => {
      const next = timerView(stateRef.current, Date.now());
      const key = JSON.stringify(next);
      if (key !== viewKey.current) {
        viewKey.current = key;
        setView(next);
      }
    };
    const read = async () => {
      const gen = genRef.current;
      const ids = idsRef.current;
      stateRef.current = startCall(stateRef.current, "read");
      const sentAt = Date.now();
      const result = await getCartHoldState(ids);
      if (!alive || gen !== genRef.current) return;
      stateRef.current = applyRead(stateRef.current, result, { sentAt, receivedAt: Date.now() }, ids);
      step();
    };
    const extend = async () => {
      const gen = genRef.current;
      const ids = idsRef.current;
      const sig = sigRef.current;
      stateRef.current = startCall(stateRef.current, "extend");
      const result = await extendCartHold(ids);
      if (!alive || gen !== genRef.current) return;
      // cart_mismatch: applyExtend has the page's rows read again and the one
      // retry wait MISMATCH_RETRY_MS; a changed cart (a Remove) restarts
      // with its own ids before that. Nothing is ever cancelled here.
      stateRef.current = applyExtend(stateRef.current, result, { receivedAt: Date.now() }, ids, sig).state;
      step();
    };
    // The device's and the monotonic clock at the last step.
    let lastWall = Date.now();
    let lastMono = performance.now();
    const step = () => {
      if (!alive) return;
      const wall = Date.now();
      const mono = performance.now();
      if (clockJumped(wall - lastWall, mono - lastMono)) stateRef.current = rereadClock(stateRef.current);
      lastWall = wall;
      lastMono = mono;
      const action = nextAction(stateRef.current, Date.now(), sigRef.current);
      if (action === "read") void read();
      else if (action === "extend") void extend();
      show();
    };
    engine.current = { step };
    step();
    const tick = window.setInterval(step, TICK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") step();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      engine.current = null;
      // A call still in flight answers into nothing: forget it, so a remount
      // (StrictMode, a re-opened drawer) is never left waiting on it.
      genRef.current += 1;
      stateRef.current = { ...stateRef.current, inFlight: null };
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // A new set of holds: judge it now (the fresh read) rather than on the tick.
  useEffect(() => {
    engine.current?.step();
  }, [cartKey]);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const onActivity = (e: Event) => {
      if (!isActivity(e)) return;
      stateRef.current = markActivity(stateRef.current, Date.now());
      engine.current?.step();
    };
    for (const type of ACTIVITY_EVENTS) form.addEventListener(type, onActivity, { capture: true, passive: true });
    return () => {
      for (const type of ACTIVITY_EVENTS) form.removeEventListener(type, onActivity, { capture: true });
    };
  }, [formRef]);

  return view;
}
